import { Link } from "react-router-dom";
import { Wordmark } from "@/components/Logo";

const EFFECTIVE_DATE = "June 15, 2026";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: "RollOSS (\u201CRollOSS\u201D, \u201Cwe\u201D, \u201Cour\u201D, \u201Cus\u201D) is a software platform that "
       + "helps Brazilian Jiu-Jitsu academies manage their schedule, roster, attendance, belt progression and student "
       + "communications. We operate from Panama. Our contact email is info@getrolloss.app.",
  },
  {
    h: "2. What data we collect",
    items: [
      "Account information you provide via Google sign-in: your name, email address, and profile picture.",
      "Information your academy provides about you (if you are a student): full name, email, belt rank, stripes, membership status, attendance records, class reservations, and any notes the academy adds.",
      "Information you generate while using RollOSS: classes you reserve, attendance check-ins, notification preferences, push notification subscriptions.",
      "Technical information automatically collected: session cookies (so you stay logged in), basic device and browser type, the IP address used to make requests.",
      "Information you choose to send us: anything you write in a support email, lead form, or invitation message.",
    ],
  },
  {
    h: "3. Why we use it",
    items: [
      "To run the service your academy purchased (or is testing) on your behalf.",
      "To deliver push notifications and class reminders that you opted into.",
      "To send transactional emails (invitations, account access, security notices).",
      "To improve the product, troubleshoot bugs and prevent abuse.",
      "To comply with legal obligations.",
    ],
  },
  {
    h: "4. Children\u2019s data",
    p: "Many BJJ academies enroll children (\u201CKids\u201D and \u201CPeewee\u201D classes). For students under 13, we rely on "
       + "the academy to obtain verifiable parental consent before adding the child to their roster. We do not "
       + "knowingly collect personal information directly from a child. If you believe a child has provided us "
       + "information without parental consent, email info@getrolloss.app and we will delete it.",
  },
  {
    h: "5. Who we share it with",
    p: "We do not sell your personal information. We share it only with the following categories of vendors, "
       + "and only to the minimum extent needed to deliver the service:",
    items: [
      "Google (for OAuth sign-in)",
      "MongoDB Atlas / our database provider (data storage)",
      "Hostinger (transactional email delivery)",
      "Browser push services (Apple, Google, Mozilla) for the delivery of push notifications you opted into",
      "Stripe (when we enable billing) — payment processing only",
      "Our hosting provider (compute and CDN)",
    ],
  },
  {
    h: "6. International transfers",
    p: "RollOSS is based in Panama. The vendors listed above may store and process your data in the United States, "
       + "the European Union or other countries with adequate privacy frameworks. By using RollOSS you consent to these transfers.",
  },
  {
    h: "7. Your rights",
    p: "Depending on where you live (GDPR for the EU, LGPD for Brazil, CCPA for California, etc.), you have the right to:",
    items: [
      "Access the personal information we hold about you",
      "Correct inaccuracies",
      "Delete your account and personal information",
      "Export your data in a portable format",
      "Object to or restrict certain processing",
      "Withdraw a consent you previously gave (e.g., for push notifications)",
    ],
    after: "To exercise any of these rights, email info@getrolloss.app. We will respond within 30 days. If you are a "
           + "student, please also contact your academy directly — they may hold their own copy of your information.",
  },
  {
    h: "8. How long we keep data",
    p: "We keep your data for as long as your account is active. If your academy deletes you from its roster, your "
       + "personal record is removed from RollOSS within 30 days. We may keep anonymous aggregate statistics indefinitely.",
  },
  {
    h: "9. Security",
    p: "We use HTTPS everywhere, hashed session tokens, allow-list authentication for owner accounts and industry-standard "
       + "encryption at rest. No system is perfectly secure, but we take reasonable measures to protect your data.",
  },
  {
    h: "10. Cookies",
    p: "RollOSS uses one essential cookie called session_token to keep you signed in. We do not use third-party tracking cookies or advertising cookies.",
  },
  {
    h: "11. Changes to this policy",
    p: "We will post any material changes to this policy on this page and update the effective date. For significant changes we will also email account owners.",
  },
  {
    h: "12. Contact us",
    p: "Questions about privacy? Email info@getrolloss.app.",
  },
];

export default function Privacy() {
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", color: "var(--text)" }}>
      <header className="px-6 md:px-8 py-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border)" }}>
        <Link to="/" data-testid="logo-link"><Wordmark /></Link>
        <Link to="/" className="label-tiny hover:text-white" data-testid="back-home">← Back to home</Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="label" style={{ color: "var(--green)" }}>LEGAL</div>
        <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2">Privacy Policy</h1>
        <div className="label-tiny mt-2" style={{ color: "var(--text-dim)" }}>Effective {EFFECTIVE_DATE}</div>

        <p className="mt-8 text-[14px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
          This Privacy Policy describes what information RollOSS collects, why we collect it, and what choices you have.
          We have tried to keep it short and in plain English. If anything is unclear, email <a href="mailto:info@getrolloss.app" style={{ color: "var(--green)" }}>info@getrolloss.app</a>.
        </p>

        <div className="mt-10 space-y-8" data-testid="privacy-sections">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="text-[18px] font-bold uppercase tracking-tight">{s.h}</h2>
              {s.p && <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--text-dim)" }}>{s.p}</p>}
              {s.items && (
                <ul className="mt-3 space-y-1.5">
                  {s.items.map((it) => (
                    <li key={it} className="text-[14px] leading-relaxed pl-5" style={{ color: "var(--text-dim)", position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: "var(--green)" }}>•</span>{it}
                    </li>
                  ))}
                </ul>
              )}
              {s.after && <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--text-dim)" }}>{s.after}</p>}
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t flex flex-wrap gap-6 text-[13px]" style={{ borderColor: "var(--border)", color: "var(--text-dim)" }}>
          <Link to="/terms" className="hover:text-white" data-testid="link-terms">Terms of Service</Link>
          <Link to="/" className="hover:text-white">getrolloss.app</Link>
          <a href="mailto:info@getrolloss.app" className="hover:text-white">info@getrolloss.app</a>
        </div>
      </main>
    </div>
  );
}
